import React, { useState } from "react";
import { useTheme } from "../../../../../contexts/ThemeContext";
import { 
  CARD_BASE, 
  INPUT_BASE, 
  SELECT_BASE, 
  LABEL_BASE,
  CARD_SPACING 
} from "../../../../../constants/styles";
import OverviewEditModal from "./OverviewEditModal";

// Add function to format ZIP code
const formatZipCode = (value) => {
  if (!value) return value;
  const zip = value.replace(/[^\d]/g, "");
  if (zip.length < 6) return zip;
  return `${zip.slice(0, 5)}-${zip.slice(5, 9)}`;
};

export default function OverviewCard({
  maritalLabel,
  taxLabel,
  filingStateLabel,
  primaryAddress,
  mailingAddress,
  marriageInfo,
  familyCount,
  maritalStatusTypes,
  taxFilingStatusOptions,
  filingStateOptions,
  onFieldChange,
  formData,
  accentColor = "border-l-2 border-l-green-600"
}) {
  const { isDarkMode } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Helper function to find state name
  const getStateName = (stateCode) => {
    const state = filingStateOptions?.find(s => 
      (s.state_code || s.code) === stateCode || 
      (s.state_id || s.id)?.toString() === stateCode?.toString()
    );
    return state?.state_name || state?.name || stateCode;
  };

  return (
    <>
      <div 
        className={`${CARD_BASE.replace('p-4', 'p-2 md:p-3')} ${accentColor} cursor-pointer group transition-all 
          hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-lg hover:scale-[1.02] relative
          hover:border-green-500 dark:hover:border-green-400 duration-200 ease-in-out`}
        onClick={() => setIsModalOpen(true)}
      >
        {/* Modern Tooltip */}
        <div 
          className="absolute -top-2 -right-2 bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium 
            opacity-0 group-hover:opacity-100 transform group-hover:translate-y-0 translate-y-4 
            transition-all duration-200 ease-in-out pointer-events-none whitespace-nowrap shadow-lg z-50"
        >
          <div className="absolute -bottom-1 right-4 w-2 h-2 bg-green-700 transform rotate-45"></div>
          View/Edit Household Overview
        </div>

        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm md:text-base font-medium text-gray-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors duration-200">Overview</h3>
        </div>

        <div className="space-y-2">
          {/* Row 1: Family Members and Dependents */}
          <div className="grid grid-cols-2 gap-x-4 text-xs">
            <div>
              <label className="text-gray-500 dark:text-gray-400">Family Members</label>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {familyCount.total} total members
              </p>
            </div>
            <div>
              <label className="text-gray-500 dark:text-gray-400">Dependents</label>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {familyCount.dependents} dependents
              </p>
            </div>
          </div>

          {/* Row 2: Marriage Information */}
          {formData.marriageInfo.marriageDate && (
            <div className="grid grid-cols-2 gap-x-4 text-xs">
              <div>
                <label className="text-gray-500 dark:text-gray-400">Marriage Date</label>
                <p className="text-gray-900 dark:text-gray-100 font-medium">
                  {formData.marriageInfo.marriageDate}
                </p>
              </div>
              <div>
                <label className="text-gray-500 dark:text-gray-400">Marriage Location</label>
                <p className="text-gray-900 dark:text-gray-100 font-medium">
                  {[
                    formData.marriageInfo.cityOfMarriage,
                    getStateName(formData.marriageInfo.stateOfMarriage)
                  ].filter(Boolean).join(", ") || "—"}
                </p>
              </div>
            </div>
          )}

          {/* Row 3: Filing Status and State */}
          <div className="grid grid-cols-2 gap-x-4 text-xs">
            <div>
              <label className="text-gray-500 dark:text-gray-400">Filing Status</label>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {taxFilingStatusOptions?.find(t => t.id?.toString() === formData.taxInfo?.filingStatus?.toString())?.name || "—"}
              </p>
            </div>
            <div>
              <label className="text-gray-500 dark:text-gray-400">Filing State</label>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {getStateName(formData.taxInfo?.filingState) || "—"}
              </p>
            </div>
          </div>

          {/* Row 4: Primary Address */}
          <div className="text-xs">
            <label className="text-gray-500 dark:text-gray-400">Primary Address</label>
            <p className="text-gray-900 dark:text-gray-100 font-medium">
              {[
                formData.address?.street,
                formData.address?.unit,
                [
                  formData.address?.city,
                  getStateName(formData.address?.state),
                  formatZipCode(formData.address?.zipCode)
                ].filter(Boolean).join(" ")
              ].filter(Boolean).join(", ") || "—"}
            </p>
          </div>

          {/* Row 5: Mailing Address (if different) */}
          {(formData.address?.mailingAddress?.street || formData.address?.mailingAddress?.city) && (
            <div className="text-xs">
              <label className="text-gray-500 dark:text-gray-400">Mailing Address</label>
              <p className="text-gray-900 dark:text-gray-100 font-medium">
                {[
                  formData.address?.mailingAddress?.street,
                  formData.address?.mailingAddress?.unit,
                  [
                    formData.address?.mailingAddress?.city,
                    getStateName(formData.address?.mailingAddress?.state),
                    formatZipCode(formData.address?.mailingAddress?.zipCode)
                  ].filter(Boolean).join(" ")
                ].filter(Boolean).join(", ") || "—"}
              </p>
            </div>
          )}
        </div>
      </div>

      <OverviewEditModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maritalStatusTypes={maritalStatusTypes}
        taxFilingStatusOptions={taxFilingStatusOptions}
        filingStateOptions={filingStateOptions}
        onFieldChange={onFieldChange}
        formData={formData}
        familyCount={familyCount}
      />
    </>
  );
}

